/**
 * LOYA Bundle System v5
 * -
 * Goal: a diamond added on its own stays a normal, standalone cart line.
 * A diamond that gets paired with a ring or pendant should show as one
 * "Your Loya Bundle" card in the cart. The cart-side display logic that
 * groups lines sharing a matching `_bundle_id` line-item property already
 * works (see snippets/cart-products.liquid) — this file is only
 * responsible for deciding *when* to write that property, correctly.
 *
 * What was broken before (v3):
 *  - "Diamond" vs "ring/pendant" pages were told apart by hardcoding two
 *    exact product handles for diamonds, and treating literally any other
 *    /products/ page as jewelry. A diamond reached via the loose-diamonds
 *    collection (a real product page, not one of the two whitelisted
 *    handles) was misread as jewelry. Every new ring or pendant handle
 *    needed a code change to keep working right.
 *  - A bundle id was generated on every visit/reload of the diamond page,
 *    not only when a diamond was actually added — revisiting the page
 *    silently orphaned whatever diamond was already sitting in the cart.
 *  - The diamond was stamped with a bundle id the moment it was added,
 *    before any ring/pendant existed to pair it with — so a solo diamond
 *    always rendered inside a "Your Loya Bundle" card of one, instead of
 *    as a normal line.
 *  - The cart line to stamp was always "whatever is cart.items[last]" —
 *    breaks if another cart mutation races it, or Shopify merges the add
 *    into an existing line instead of appending a new one.
 *
 * v5 fixes all four:
 *  - Diamond / ring / pendant pages are identified by product tag
 *    ("diamond" / "ring" / "pendant" — tag every current and future
 *    product accordingly and this keeps working, no code change needed),
 *    with product.type as a fallback for anything not yet tagged.
 *  - The diamond page never calls the cart API at all. Confirming a
 *    diamond add just remembers "this product id is waiting to be
 *    paired" in sessionStorage.
 *  - A diamond is ONLY ever stamped with `_bundle_id` at the moment a
 *    ring/pendant is added afterwards, and both lines are stamped with
 *    the same freshly generated id together. A diamond bought alone
 *    never gets a `_bundle_id`, so it renders as a plain line — never a
 *    one-item bundle card.
 *  - Cart lines are located by matching the product that was just added
 *    AND has no `_bundle_id` yet (scanned from the newest line
 *    backwards), not by array position.
 *  - Listens specifically for Horizon's CartAddEvent (real additions),
 *    not the more general cart:update, so quantity changes/removals on
 *    unrelated lines can't misfire a stamp.
 *
 * v6 (Oct 2026):
 *  - FIX: the Horizon update removed `CartAddEvent` and
 *    `ThemeEvents.cartUpdate` from @theme/events. Importing a name that
 *    no longer exists makes the browser refuse to run this whole file,
 *    so pairing, the checkout gate, the story form check and Remove
 *    Bundle all silently stopped. Now listens to Shopify's standard
 *    `cart:lines-update` event (StandardEvents.cartLinesUpdate), which
 *    is what Horizon itself uses now.
 *  - Every diamond / ring / pendant add carries a unique hidden
 *    `_loya_ref` property. That guarantees each piece is its own cart
 *    line (two identical diamonds never merge into one line with qty 2)
 *    and lets us find the exact diamond to pair by ref, not by
 *    "same product id", which picked the wrong diamond whenever a loose
 *    diamond of the same product was already in the cart.
 *  - "Loose Diamond" on the chooser page now closes that diamond out:
 *    it stays a standalone line and is no longer "waiting" to be paired.
 *  - "Create Another LOYA Piece" on the cart page starts a fresh
 *    diamond -> setting flow without touching what's already in the cart.
 *  - The "Add a Diamond" popup removes only the unpaired ring/pendant
 *    lines instead of clearing the entire cart.
 *
 * v5.1 adds cut matching:
 *  - Remembers the "Cut" chosen on the diamond (Round / Oval / Princess /
 *    Emerald / Pear / Heart) in sessionStorage the moment the diamond is
 *    added to cart.
 *  - On /pages/choose-your-jewelry, rewrites the Rings and Pendants
 *    "Select" button links to the collection holding only that cut
 *    (e.g. "rings-round-cut"), instead of the general Rings/Pendant
 *    collection. See CUT_COLLECTION_HANDLES below for the handle list.
 *  - Falls back to the original, unfiltered Rings/Pendant links whenever
 *    no cut is known or that cut isn't in the handle list, so this can
 *    never send a customer to a broken/empty page.
 */

import { StandardEvents } from '@shopify/events';

const DIAMOND_TAG = 'diamond';
const RING_TAG = 'ring';
const PENDANT_TAG = 'pendant';

const PENDING_DIAMOND_KEY = 'loya_pending_diamond_product_id';
const BUNDLE_COUNTER_KEY = 'loya_bundle_counter';
const SELECTED_CUT_KEY = 'loya_selected_cut';

// Hidden line-item property written on every diamond / ring / pendant
// add. Leading underscore = hidden in cart, checkout and order emails.
const REF_PROPERTY = '_loya_ref';

// Where a customer lands right after a diamond is confirmed added to the
// cart, for every diamond-tagged product (not just one specific handle).
// Change this one line if that page's URL ever changes.
const DIAMOND_NEXT_URL = '/pages/choose-your-jewelry';

// ---------------------------------------------------------------------
// Cut matching: after a diamond is added to the cart, remember which
// "Cut" the customer picked (Round / Oval / Princess / Emerald / Pear /
// Heart) so that on the /pages/choose-your-jewelry screen, the Rings and
// Pendants "Select" buttons can be pointed at the collection holding
// only that cut, instead of the general Rings/Pendant collection.
//
// Each cut has its own collection per jewelry type, e.g.
// "rings-round-cut" and "pendants-round-cut". If a collection's real
// handle in Shopify ever comes out different from what's listed below,
// fix the string on that one line — nothing else in this file needs to
// change. Adding a 7th cut later means adding one more entry to each of
// these two objects (plus creating the matching collection in Shopify).
// ---------------------------------------------------------------------

const CUT_COLLECTION_HANDLES = {
  rings: {
    round: 'rings-round-cut',
    oval: 'rings-oval-cut',
    princess: 'rings-princess-cut',
    radiant: 'rings-radiant-cut',
    emerald: 'rings-emerald-cut',
    pear: 'rings-pear-cut',
    heart: 'rings-heart-cut',
    cushion: 'rings-cushion-cut',
  },
  pendants: {
    round: 'pendants-round-cut',
    oval: 'pendants-oval-cut',
    princess: 'pendants-princess-cut',
    radiant: 'pendants-radiant-cut',
    emerald: 'pendants-emerald-cut',
    pear: 'pendants-pear-cut',
    heart: 'pendants-heart-cut',
    cushion: 'pendants-cushion-cut',
  },
};

// The path of the "Choose your jewelry" tile page (Rings / Pendants /
// Diamond Only), where the cut-based redirect happens. Change this one
// line if that page's URL ever changes.
const JEWELRY_CHOOSER_PATH = '/pages/choose-your-jewelry';

// ---------------------------------------------------------------------
// v7 (Oct 2026): translation support (Translate & Adapt).
// On a translated storefront Shopify puts the language in front of
// every path (/nl/pages/..., /nl/collections/...), and the cart API
// returns option names/values in that language ("Slijpvorm: Rond"
// instead of "Cut: Round"). Everything below compares paths with the
// language prefix stripped, sends customers to URLs that keep their
// language, and reads the cut from the store's default language.
// ---------------------------------------------------------------------

/** Language root of the current storefront: "/" or e.g. "/nl/". */
function getLocaleRoot() {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  if (typeof root !== 'string' || !root) return '/';
  return root.endsWith('/') ? root : root + '/';
}

/** "/nl/pages/x/" -> "/pages/x". Leaves default-language paths as is. */
function normalizePath(pathname) {
  let path = String(pathname || '');
  const root = getLocaleRoot();
  if (root !== '/') {
    const prefix = root.replace(/\/+$/, '');
    if (path === prefix) path = '/';
    else if (path.indexOf(prefix + '/') === 0) path = path.slice(prefix.length);
  }
  return path.replace(/\/+$/, '') || '/';
}

/** "/cart" -> "/nl/cart" on the Dutch storefront, "/cart" otherwise. */
function localizeUrl(path) {
  return getLocaleRoot() + String(path).replace(/^\/+/, '');
}

function getProductContext() {
  return window.LoyaProductContext || null;
}

function hasTag(context, tag) {
  if (!context || !Array.isArray(context.tags)) return false;
  return context.tags.some((t) => typeof t === 'string' && t.toLowerCase() === tag);
}

function hasType(context, type) {
  return !!(context && typeof context.type === 'string' && context.type.toLowerCase() === type);
}

function isOnDiamondPage() {
  const context = getProductContext();
  if (!context) return false;
  if (hasTag(context, DIAMOND_TAG)) return true;
  // Fallback for products not yet tagged: match the admin "Type" field,
  // but only if it isn't already tagged/typed as jewelry.
  return !hasTag(context, RING_TAG) && !hasTag(context, PENDANT_TAG) && hasType(context, DIAMOND_TAG);
}

function isOnJewelryPage() {
  const context = getProductContext();
  if (!context) return false;
  if (hasTag(context, RING_TAG) || hasTag(context, PENDANT_TAG)) return true;
  return hasType(context, RING_TAG) || hasType(context, PENDANT_TAG);
}

function getNextBundleId() {
  let counter = parseInt(localStorage.getItem(BUNDLE_COUNTER_KEY) || '0', 10);
  counter += 1;
  localStorage.setItem(BUNDLE_COUNTER_KEY, counter);
  return `loya_bundle_${counter}`;
}

/**
 * Pending diamond = the diamond that was just added and is waiting for a
 * ring/pendant. Stored as { productId, ref }. Older sessions may still
 * hold a bare product id from v5, which is read back as { productId }.
 */
function setPendingDiamond(productId, ref) {
  sessionStorage.setItem(PENDING_DIAMOND_KEY, JSON.stringify({ productId: Number(productId), ref: ref || null }));
}

function getPendingDiamond() {
  const value = sessionStorage.getItem(PENDING_DIAMOND_KEY);
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object') return parsed;
    if (typeof parsed === 'number') return { productId: parsed, ref: null };
  } catch (e) {
    /* fall through */
  }
  return null;
}

function clearPendingDiamond() {
  sessionStorage.removeItem(PENDING_DIAMOND_KEY);
}

/**
 * Turns a product option value ("Round", "Lab-Grown Pear", etc.) into a
 * lowercase, hyphenated slug ("round", "lab-grown-pear") so it can be
 * matched against the keys in CUT_COLLECTION_HANDLES regardless of exact
 * capitalization or spacing.
 */
function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Reads the "Cut" option value off a cart line item and slugifies it.
 * Relies on `options_with_values`, which Shopify's cart API includes on
 * every line item (each entry is { name, value } for one product
 * option). Returns null if the item has no option literally named "Cut"
 * (case-insensitive) — callers should treat that as "couldn't determine
 * a cut" and leave the next page showing everything, not throw.
 */
function getSelectedCutFromCartItem(item) {
  if (!item || !Array.isArray(item.options_with_values)) return null;
  const cutOption = item.options_with_values.find(function (opt) {
    return opt && typeof opt.name === 'string' && ['cut', 'shape'].indexOf(opt.name.toLowerCase()) !== -1;
  });
  return cutOption ? slugify(cutOption.value) : null;
}

function isKnownCut(cutSlug) {
  return !!cutSlug && (cutSlug in CUT_COLLECTION_HANDLES.rings || cutSlug in CUT_COLLECTION_HANDLES.pendants);
}

// The store's default (source) language. Option names/values are read
// in this language, where they are literally "Shape: Round".
const DEFAULT_LANGUAGE = 'en';

/**
 * URL roots that serve the store's DEFAULT language for the shopper's
 * current market. With Markets, the bare root ("/products/x.js") is
 * redirected to the shopper's market + language (e.g. /nl-nl/), so it
 * comes back in Dutch. The default-language version of the Netherlands
 * market lives at /en-nl/, so that is tried first.
 */
function getDefaultLanguageRoots() {
  const roots = [];
  const root = getLocaleRoot(); // e.g. "/nl-nl/", "/nl/", "/"
  const country = window.Shopify && typeof window.Shopify.country === 'string' ? window.Shopify.country.toLowerCase() : '';
  const marketMatch = root.match(/^\/[a-z]{2,3}(?:-[a-z]+)?-([a-z]{2})\/$/i);
  const marketCountry = marketMatch ? marketMatch[1].toLowerCase() : country;
  if (root !== '/' && marketCountry) roots.push('/' + DEFAULT_LANGUAGE + '-' + marketCountry + '/');
  roots.push('/');
  return roots.filter(function (r, i) {
    return roots.indexOf(r) === i;
  });
}

function findCutOptionIndex(product) {
  if (!product || !Array.isArray(product.options)) return -1;
  return product.options.findIndex(function (opt) {
    const name = typeof opt === 'string' ? opt : opt && opt.name;
    return typeof name === 'string' && ['cut', 'shape'].indexOf(name.trim().toLowerCase()) !== -1;
  });
}

/**
 * Translation-proof cut lookup. Fetches the product in the store's
 * DEFAULT language (where the option is still "Shape: Round") and reads
 * the value for the exact variant that was added. Resolves to a slug or
 * null.
 */
async function fetchDefaultLocaleCut(item) {
  const context = getProductContext();
  const handle = (item && item.handle) || (context && context.handle);
  if (!handle || !item) return null;
  const variantId = Number(item.variant_id || item.id);
  const roots = getDefaultLanguageRoots();

  for (let i = 0; i < roots.length; i++) {
    try {
      const res = await fetch(roots[i] + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) continue;
      const product = await res.json();
      const index = findCutOptionIndex(product);
      if (index === -1 || !Array.isArray(product.variants)) continue; // still translated, try next root
      const variant = product.variants.find(function (v) {
        return Number(v.id) === variantId;
      });
      if (!variant || !Array.isArray(variant.options)) continue;
      const slug = slugify(variant.options[index]);
      if (isKnownCut(slug)) return slug;
    } catch (e) {
      /* try next root */
    }
  }
  return null;
}

/**
 * Current-language read first (instant, works in English), then the
 * default-language lookup if that didn't give a known cut.
 */
function resolveSelectedCut(item) {
  const local = getSelectedCutFromCartItem(item);
  if (isKnownCut(local)) return Promise.resolve(local);
  return fetchDefaultLocaleCut(item).then(function (fromDefault) {
    return fromDefault || local || null;
  });
}

function setSelectedCut(cutSlug) {
  sessionStorage.setItem(SELECTED_CUT_KEY, cutSlug);
}

function getSelectedCut() {
  return sessionStorage.getItem(SELECTED_CUT_KEY);
}

function clearSelectedCut() {
  sessionStorage.removeItem(SELECTED_CUT_KEY);
}

function getCartSectionId() {
  const el = document.querySelector('cart-items-component[data-section-id]');
  return el ? el.dataset.sectionId : null;
}

// ---------------------------------------------------------------------
// One add per visit: once this page's product has been confirmed in the
// cart, lock Add to Cart (and accelerated checkout) so a second click
// can't add a duplicate diamond / duplicate ring or pendant. Horizon
// re-enables and re-morphs the button whenever the variant selection
// changes (see product-form.js #onVariantUpdate), so the lock has to be
// re-applied on every variant:update too, not just set once.
// ---------------------------------------------------------------------

function applyAddToCartLock() {
  document.querySelectorAll('add-to-cart-component').forEach(function (el) {
    if (typeof el.disable === 'function') el.disable();
  });
  document.querySelectorAll('.accelerated-checkout-block').forEach(function (block) {
    block.style.display = 'none';
  });
}

/**
 * Finds a cart line for a given product id that hasn't been stamped with
 * a _bundle_id yet, scanning from the newest line backwards. Used both
 * for "find the line that was just added on this page" (pass the current
 * page's product id) and "find the diamond line waiting to be paired"
 * (pass the remembered pending diamond product id).
 */
function findUnstampedLineForProduct(cart, productId) {
  // Shopify lists the most recently added line FIRST (items[0]), so scan
  // from the front to get the newest match. (v5 scanned from the end,
  // which returned the OLDEST matching line, e.g. a loose diamond that
  // was already in the cart instead of the one just added.)
  const items = (cart && cart.items) || [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.product_id !== productId) continue;
    if (item.properties && item.properties['_bundle_id']) continue;
    return { item, line: i + 1 };
  }
  return null;
}

/**
 * Finds the cart line carrying our unique `_loya_ref`. This is the
 * primary lookup; findUnstampedLineForProduct is only the fallback for
 * an add where the ref didn't make it onto the line (e.g. an app that
 * rebuilds the add-to-cart request itself).
 */
function findLineByRef(cart, ref, productId) {
  if (ref) {
    const items = (cart && cart.items) || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.properties && item.properties[REF_PROPERTY] === ref) {
        if (item.properties['_bundle_id']) return null; // already paired
        return { item, line: i + 1 };
      }
    }
  }
  return productId ? findUnstampedLineForProduct(cart, productId) : null;
}

// ---------------------------------------------------------------------
// Unique ref per add. One ref per page load: the page redirects right
// after a successful add, so there is never a second add with the same
// ref. Re-applied at submit/click time because Horizon re-renders the
// product form on variant change, which would drop an input added once.
// ---------------------------------------------------------------------

function makeRef(prefix) {
  return prefix + '_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function ensureRefInputs(ref) {
  document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
    let input = form.querySelector('input[name="properties[' + REF_PROPERTY + ']"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + REF_PROPERTY + ']';
      form.appendChild(input);
    }
    input.value = ref;
  });
}

function installRefInputs(ref) {
  ensureRefInputs(ref);
  // Capture phase: runs before Horizon's own submit handler (and before
  // most apps' click handlers) so the hidden input is always there when
  // the form data is read.
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (form && form.matches && form.matches('form[action*="/cart/add"]')) ensureRefInputs(ref);
  }, true);
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('add-to-cart-component, [name="add"], sticky-add-to-cart')) {
      ensureRefInputs(ref);
    }
  }, true);
}

/**
 * Stamps _bundle_id onto a cart line, requesting fresh section HTML in
 * the same request so it can be handed straight to Horizon's morph
 * renderer.
 */
async function stampBundleId(line, bundleId, existingProperties, sectionId) {
  const body = {
    line,
    properties: Object.assign({}, existingProperties, {
      _bundle_id: bundleId,
    }),
  };

  if (sectionId) {
    body.sections = sectionId;
    body.sections_url = window.location.pathname;
  }

  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  return res.json();
}

/**
 * Listens for real cart additions via Shopify's cart:lines-update event (fired by
 * product-form-component / quick-add / sticky-add-to-cart right after
 * Shopify confirms the add), never a generic cart:update, so quantity
 * changes and removals elsewhere in the cart can't trigger a stamp.
 */
function onCartAdd(handler) {
  // Horizon now dispatches Shopify's standard `cart:lines-update` event
  // with action 'add' BEFORE the request finishes, plus a promise that
  // resolves once Shopify confirms the add (with the fresh cart lines).
  document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
    if (event.action !== 'add' || !event.promise) return;

    event.promise
      .then(function (result) {
        const detail = result && result.detail;
        if (!detail || detail.didError) return;
        if (!Array.isArray(detail.items) || detail.items.length === 0) return;
        handler({ items: detail.items });
      })
      .catch(function () {
        /* add failed: Horizon already shows the error */
      });
  });
}

function initDiamondPage() {
  const context = getProductContext();
  const ref = makeRef('d');
  installRefInputs(ref);

  onCartAdd(async function (cart) {
    // Confirm the diamond actually landed in the cart before remembering
    // it as "pending" — don't trust the click alone.
    const target = findLineByRef(cart, ref, context.id);
    if (!target) return;

    // Only remember the ref if it really made it onto the line;
    // otherwise pairing falls back to matching by product id.
    const landedRef = target.item.properties && target.item.properties[REF_PROPERTY] === ref ? ref : null;
    setPendingDiamond(context.id, landedRef);

    // Remember which cut was just chosen (Round / Oval / Princess /
    // Emerald / Pear / Heart) so the next page can send the customer to
    // the ring/pendant collection for that specific cut. If the line
    // item has no option literally named "Cut" — a new diamond product
    // set up without that option, a typo, etc. — this quietly clears
    // any previously remembered cut instead of leaving a stale one
    // behind, and the next page just falls back to showing every
    // ring/pendant.
    // Lock right away; the cut lookup below may take one quick request.
    applyAddToCartLock();
    const cutSlug = await resolveSelectedCut(target.item);
    if (cutSlug) {
      setSelectedCut(cutSlug);
    } else {
      clearSelectedCut();
      console.warn(
        '[LOYA] Could not find a "Cut" option on the diamond that was just added — the next page will show every ring/pendant instead of filtering by cut.'
      );
    }

    // Lock the button immediately so a click in the moment before the
    // page navigates away can't add a second diamond, then move the
    // customer straight on to choosing their setting.
    applyAddToCartLock();
    window.location.href = localizeUrl(DIAMOND_NEXT_URL);
  });
}

// ---------------------------------------------------------------------
// Choose-your-jewelry page: point the Rings / Pendants "Select" buttons
// at the collection matching the cut chosen on the previous page,
// instead of the general Rings/Pendant collection. Runs only on
// JEWELRY_CHOOSER_PATH. Never touches the "Diamond Only" button.
// ---------------------------------------------------------------------

/**
 * Finds every on-page link whose href resolves to the given collection
 * path (e.g. "/collections/rings"), regardless of query string or
 * trailing slash. Matched by current href rather than by button text or
 * position, so this keeps working through normal page-builder edits as
 * long as the link still points at that collection.
 *
 * Scoped to #MainContent (the page body Shopify renders inside <main>)
 * rather than the whole document — the header's own navigation menu
 * very likely has its own link to the same /collections/rings and
 * /collections/pendant paths, and matching document-wide would find
 * that menu link (it comes before the page content in the DOM) and
 * rewrite it instead of the actual "Select" button, leaving the real
 * button untouched. Returns an array (not just the first match) in
 * case the section renders more than one copy of the button (e.g. a
 * separate mobile layout hidden with CSS) — all of them need rewriting,
 * not just whichever appears first.
 */
function findCollectionLinks(collectionPath) {
  const scope = document.getElementById('MainContent') || document;
  const links = scope.querySelectorAll('a[href]');
  const matches = [];
  links.forEach(function (link) {
    let path;
    try {
      path = normalizePath(new URL(link.href, window.location.origin).pathname);
    } catch (e) {
      return;
    }
    if (path === collectionPath) matches.push(link);
  });
  return matches;
}

function initCutRedirectPage() {
  const cutSlug = getSelectedCut();
  // No cut remembered (customer landed here directly, storage was
  // cleared, etc.) — leave both buttons pointing at their original,
  // general Rings/Pendant collections. This is the safety net: nothing
  // to do here means nothing breaks.
  if (!cutSlug) return;

  [
    { type: 'rings', collectionPath: '/collections/rings' },
    { type: 'pendants', collectionPath: '/collections/pendant' },
  ].forEach(function (entry) {
    const handle = CUT_COLLECTION_HANDLES[entry.type][cutSlug];
    // Cut not in the map (new cut added on the diamond product but its
    // collections/handles weren't added here yet) — leave this button
    // alone rather than send customers to a collection that may not
    // exist.
    if (!handle) return;

    const links = findCollectionLinks(entry.collectionPath);
    links.forEach(function (link) {
      link.setAttribute('href', localizeUrl('/collections/' + handle));
    });
  });
}

/**
 * "Loose Diamond" tile/button on the chooser page (it links to /cart).
 * Choosing it means "this diamond is finished, no setting". Forget it as
 * pending so a ring/pendant added later (for a NEW diamond) can never be
 * paired with this loose one. The diamond line itself is left untouched,
 * so it stays a normal standalone line in the cart.
 */
function initLooseDiamondChoice() {
  const scope = document.getElementById('MainContent') || document;
  scope.addEventListener('click', function (e) {
    const link = e.target.closest && e.target.closest('a[href]');
    if (!link) return;
    let path;
    try {
      path = normalizePath(new URL(link.href, window.location.origin).pathname);
    } catch (err) {
      return;
    }
    if (path !== '/cart') return;
    clearPendingDiamond();
    clearSelectedCut();
  });
}

/**
 * "Create Another LOYA Piece" on the cart page. Starts the flow over at
 * diamond selection WITHOUT clearing the cart, and makes sure nothing
 * from the previous piece is still remembered as pending.
 */
function initStartNewPiece() {
  document.addEventListener('click', function (e) {
    const link = e.target.closest && e.target.closest('[data-loya-start-new-piece]');
    if (!link) return;
    clearPendingDiamond();
    clearSelectedCut();
  });
}

function initJewelryPage() {
  const ref = makeRef('j');
  installRefInputs(ref);

  // Rings and pendants can be added to the cart on their own — nothing is
  // blocked here. Whether a diamond is required is checked at checkout
  // time instead (see installCheckoutGate below), not at add-to-cart time.
  onCartAdd(async function (cart) {
    const context = getProductContext();
    const jewelryTarget = findLineByRef(cart, ref, context.id);
    if (!jewelryTarget) return;

    // Lock immediately so a click in the moment before navigation can't
    // add a second ring/pendant.
    applyAddToCartLock();

    // If a diamond was chosen earlier this session and is still sitting
    // unstamped in the cart, pair it with this ring/pendant now. If none
    // was chosen (or it was removed after being flagged as pending), the
    // ring/pendant still goes to the cart as a normal standalone line —
    // installCheckoutGate() is what stops checkout without a diamond, not
    // this step.
    const pending = getPendingDiamond();
    const diamondTarget = pending ? findLineByRef(cart, pending.ref, pending.productId) : null;
    clearPendingDiamond();
    clearSelectedCut();

    if (diamondTarget) {
      const sectionId = getCartSectionId();
      const bundleId = getNextBundleId();

      // Both stamps have to finish (awaited) before we navigate away —
      // navigating to /cart would otherwise abort whichever fetch is
      // still in flight and leave the pairing half-done.
      let result = await stampBundleId(jewelryTarget.line, bundleId, jewelryTarget.item.properties || {}, sectionId);
      if (result && !result.status) {
        const diamondTargetAfter = findLineByRef(result, pending.ref, pending.productId);
        if (diamondTargetAfter) {
          await stampBundleId(diamondTargetAfter.line, bundleId, diamondTargetAfter.item.properties || {}, sectionId);
        }
      }
    }

    window.location.href = localizeUrl('/cart');
  });
}

// ---------------------------------------------------------------------
// Diamond-required gate: rings and pendants can be added to the cart
// freely, but the cart can't check out while it holds a ring/pendant and
// zero diamonds. Runs on every page (not just product pages), because
// the cart drawer — and its Checkout / Shop Pay buttons — can be opened
// from anywhere, not only on the dedicated /cart page.
//
// This checks LIVE cart contents (via the data-loya-needs-diamond flag
// snippets/cart-products.liquid recomputes on every render), not a
// stamped property on the ring/pendant line — a _bundle_id-based check
// would keep treating the ring as "fine" even after its paired diamond
// line gets deleted from the cart, since the stamp is never removed.
// ---------------------------------------------------------------------

const RESET_FLOW_URL = '/pages/create-your-diamond';

function cartNeedsDiamond() {
  const form = document.getElementById('cart-form');
  return !!form && form.getAttribute('data-loya-needs-diamond') === 'true';
}

function applyCheckoutGate() {
  const blocked = cartNeedsDiamond() || !isStoryFormComplete();
  document.querySelectorAll('.additional-checkout-buttons').forEach(function (block) {
    block.style.display = blocked ? 'none' : '';
  });
}

function getUnpairedJewelryKeys() {
  const form = document.getElementById('cart-form');
  const raw = form ? form.getAttribute('data-loya-unpaired-keys') || '' : '';
  return raw.split(',').filter(Boolean);
}

/**
 * Removes ONLY the rings/pendants that have no diamond (keys rendered by
 * snippets/cart-products.liquid), keeps everything else (loose diamonds,
 * finished bundles), and sends the customer to the start of the
 * diamond-picking flow. v5 emptied the whole cart here, which would also
 * wipe a loose diamond or a finished bundle. Redirects even if the
 * removal fails, so a network hiccup can't strand the customer.
 */
function resetToChooseDiamond() {
  const keys = getUnpairedJewelryKeys();
  const updates = {};
  keys.forEach(function (key) {
    updates[key] = 0;
  });

  clearPendingDiamond();
  clearSelectedCut();

  const request = keys.length
    ? fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
      })
    : Promise.resolve();

  request
    .catch(function (error) {
      console.error('[LOYA] Failed to remove unpaired setting before reset', error);
    })
    .finally(function () {
      window.location.href = localizeUrl(RESET_FLOW_URL);
    });
}

/**
 * Explains why checkout is blocked, with a button that performs the
 * clear-cart-and-redirect. Closing the popup without clicking the button
 * leaves the cart exactly as it was, so the customer can edit it
 * themselves (e.g. remove the ring/pendant) instead of being forced
 * straight into the reset.
 */
/**
 * Popup text in the shopper's language. Strings come from the theme
 * locale files ("loya.gate.*", rendered into window.LoyaStrings by
 * snippets/scripts.liquid); English is the fallback.
 */
function t(key, fallback) {
  const strings = window.LoyaStrings || {};
  const value = strings['gate_' + key];
  return typeof value === 'string' && value && value.indexOf('translation missing') === -1 ? value : fallback;
}

function showDiamondRequiredModal() {
  if (document.querySelector('.loya-diamond-gate')) return;

  const overlay = document.createElement('div');
  overlay.className = 'loya-diamond-gate';
  overlay.innerHTML =
    '<div class="loya-diamond-gate__backdrop"></div>' +
    '<div class="loya-diamond-gate__panel" role="dialog" aria-modal="true" aria-labelledby="loya-gate-title">' +
    '<p class="loya-diamond-gate__eyebrow">' + t('eyebrow', 'Every LOYA piece begins with its diamond') + '</p>' +
    '<p class="loya-diamond-gate__title" id="loya-gate-title">' + t('title', 'Let\'s Start the Pairing Again') + '</p>' +
    '<p class="loya-diamond-gate__body">' + t('body', 'Your ring or pendant needs a diamond paired with it before you can check out. Please choose your diamond first, then select the setting it will live in.') + '</p>' +
    '<ol class="loya-diamond-gate__steps">' +
    '<li><span>1</span>' + t('step1', 'Choose your diamond') + '</li>' +
    '<li><span>2</span>' + t('step2', 'Choose your ring or pendant') + '</li>' +
    '<li><span>3</span>' + t('step3', 'Tell us your story and check out') + '</li>' +
    '</ol>' +
    '<p class="loya-diamond-gate__note">' + t('note', 'The unpaired setting will be removed from your cart so you can begin again. Anything else in your cart stays as it is.') + '</p>' +
    '<div class="loya-diamond-gate__actions">' +
    '<button type="button" class="button loya-diamond-gate__cta">' + t('cta', 'Start Pairing Again') + '</button>' +
    '</div>' +
    '</div>';

  document.body.appendChild(overlay);

  function close() {
    overlay.remove();
  }

  overlay.querySelector('.loya-diamond-gate__backdrop').addEventListener('click', close);
  overlay.querySelector('.loya-diamond-gate__cta').addEventListener('click', function (e) {
    e.currentTarget.disabled = true;
    e.currentTarget.textContent = t('cta_loading', 'Taking you to the diamonds...');
    resetToChooseDiamond();
  });
  overlay.querySelector('.loya-diamond-gate__cta').focus();
  document.addEventListener('keydown', function onKey(e) {
    if (e.key === 'Escape') {
      close();
      document.removeEventListener('keydown', onKey);
    }
  });
}

// ---------------------------------------------------------------------
// Cart-page story form: "Who is this for / their name / what you're
// sending / their story." Required before checkout, same as the diamond
// requirement — checked in the same #cart-form submit handler below.
// Only rendered on the /cart page (snippets/loya-diamond-story-form.liquid,
// included from sections/main-cart.liquid), so on any other page
// getStoryForm() returns null and every check here is a harmless no-op.
// Saved as the cart note (becomes the order note) — debounced as the
// customer types, and forced once more right before checkout, so an
// abandoned cart still keeps whatever was answered, and checkout never
// proceeds on stale data.
// ---------------------------------------------------------------------

function getStoryForm() {
  return document.getElementById('loya-story-form');
}

function getStoryFieldValue(fieldName) {
  if (fieldName === 'name') {
    const input = document.getElementById('loya-story-name');
    return input ? input.value.trim() : '';
  }
  if (fieldName === 'date') {
    const input = document.getElementById('loya-story-date');
    return input ? input.value.trim() : '';
  }
  if (fieldName === 'sending') {
    const checked = document.querySelector('input[name="loya-story-sending"]:checked');
    return checked ? checked.value : '';
  }
  if (fieldName === 'story') {
    const textarea = document.getElementById('loya-story-text');
    return textarea ? textarea.value.trim() : '';
  }
  if (fieldName === 'notes') {
    const textarea = document.getElementById('loya-story-notes');
    return textarea ? textarea.value.trim() : '';
  }
  return '';
}

/**
 * @returns {Record<'who'|'who-other'|'name'|'date'|'sending'|'story'|'consent', boolean>}
 */
function getStoryFieldStatus() {
  const whoSelect = document.getElementById('loya-story-who');
  const whoValue = whoSelect ? whoSelect.value : '';
  const needsOther = whoValue === 'Someone else';
  const whoOtherInput = document.getElementById('loya-story-who-other');
  const whoOtherValue = whoOtherInput ? whoOtherInput.value.trim() : '';
  const consentBox = document.getElementById('loya-story-consent');

  return {
    who: !!whoValue,
    'who-other': !needsOther || !!whoOtherValue,
    name: !!getStoryFieldValue('name'),
    date: !!getStoryFieldValue('date'),
    sending: !!getStoryFieldValue('sending'),
    story: !!getStoryFieldValue('story'),
    consent: !!(consentBox && consentBox.checked),
  };
}

function isStoryFormComplete() {
  if (!getStoryForm()) return true; // not on this page — nothing to gate on
  const status = getStoryFieldStatus();
  return Object.keys(status).every(function (key) {
    return status[key];
  });
}

function updateStoryFormErrorDisplay() {
  const form = getStoryForm();
  if (!form) return;

  const status = getStoryFieldStatus();
  let anyError = false;

  Object.keys(status).forEach(function (key) {
    const wrapper = form.querySelector('[data-loya-field="' + key + '"]');
    if (!wrapper) return;
    const hasError = !status[key];
    wrapper.classList.toggle('loya-story-form__field--error', hasError);
    if (hasError) anyError = true;
  });

  const statusEl = document.getElementById('loya-story-form-status');
  if (statusEl) statusEl.hidden = !anyError;
}

function composeStoryNote() {
  const whoSelect = document.getElementById('loya-story-who');
  let whoText = whoSelect ? whoSelect.value : '';
  if (whoText === 'Someone else') {
    const other = document.getElementById('loya-story-who-other');
    const otherText = other ? other.value.trim() : '';
    whoText = otherText ? 'Someone else \u2014 ' + otherText : 'Someone else';
  }

  const consentBox = document.getElementById('loya-story-consent');
  const notes = getStoryFieldValue('notes');

  const lines = [
    'Who this is for: ' + (whoText || '\u2014'),
    'Their name: ' + (getStoryFieldValue('name') || '\u2014'),
    'A date that means something: ' + (getStoryFieldValue('date') || '\u2014'),
    'Sending: ' + (getStoryFieldValue('sending') || '\u2014'),
    'Their story: ' + (getStoryFieldValue('story') || '\u2014'),
  ];

  if (notes) {
    lines.push('Anything else: ' + notes);
  }

  lines.push('Custom production terms agreed: ' + (consentBox && consentBox.checked ? 'Yes' : 'No'));

  return lines.join('\n');
}

function saveStoryNote() {
  if (!getStoryForm()) return Promise.resolve();

  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: composeStoryNote() }),
  }).catch(function (error) {
    console.error('[LOYA] Failed to save story form to cart note', error);
  });
}

function initStoryForm() {
  const form = getStoryForm();
  if (!form) return;

  const whoSelect = document.getElementById('loya-story-who');
  const whoOtherWrapper = form.querySelector('[data-loya-field="who-other"]');

  function syncOtherVisibility() {
    const isOther = !!whoSelect && whoSelect.value === 'Someone else';
    if (whoOtherWrapper) whoOtherWrapper.hidden = !isOther;
  }
  syncOtherVisibility();
  if (whoSelect) whoSelect.addEventListener('change', syncOtherVisibility);

  let saveTimeout;
  function scheduleSave(delay) {
    clearTimeout(saveTimeout);
    saveTimeout = setTimeout(saveStoryNote, delay);
  }

  form.addEventListener('input', function () {
    updateStoryFormErrorDisplay();
    scheduleSave(800);
  });
  form.addEventListener('change', function () {
    updateStoryFormErrorDisplay();
    scheduleSave(200);
  });
}

/**
 * Scrolls the story form into view and focuses the first incomplete
 * field, so the customer isn't just shown a status message with no clue
 * which field it refers to.
 */
function focusFirstIncompleteStoryField() {
  const form = getStoryForm();
  if (!form) return;
  const firstError = form.querySelector('.loya-story-form__field--error');
  if (!firstError) return;
  firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const field = firstError.querySelector('input, select, textarea');
  if (field) field.focus();
}

// ---------------------------------------------------------------------
// Cart-page bundle removal: a diamond + ring/pendant bundle is removed
// as one unit via a single button in the bundle header (see
// snippets/cart-products.liquid), instead of deleting each line one at
// a time. Individual lines inside a bundle no longer render their own
// remove button or quantity stepper at all (see the same file) — this
// is the only way to remove a bundle line.
// ---------------------------------------------------------------------

/**
 * Removes every cart line carrying the given _bundle_id. Reads the live
 * cart first rather than trusting the DOM, since properties (and which
 * lines exist) can only be trusted from a fresh /cart.js fetch. Targets
 * lines by their unique `key`, not position, since position shifts as
 * earlier lines are removed mid-loop.
 */
async function removeBundle(bundleId, button) {
  if (button) {
    button.disabled = true;
    button.classList.add('loya-bundle-remove--loading');
  }

  try {
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();
    const targets = (cart.items || []).filter(function (item) {
      return item.properties && item.properties['_bundle_id'] === bundleId;
    });

    if (targets.length === 0) {
      window.location.reload();
      return;
    }

    // One atomic /cart/update.js call with every line keyed to quantity
    // 0, instead of N sequential /cart/change.js calls. Sequential calls
    // raced against each other server-side and could silently leave one
    // line (usually the diamond) behind — a single update request has
    // no such race.
    const updates = {};
    targets.forEach(function (item) {
      updates[item.key] = 0;
    });

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    });

    if (!res.ok) {
      throw new Error('Cart update failed with status ' + res.status);
    }

    // A full reload is the simplest reliable way to get the cart page
    // (grouping, checkout-gate flag, totals) back in sync — this is a
    // dedicated /cart page, not a drawer that needs to stay open.
    window.location.reload();
  } catch (error) {
    console.error('[LOYA] Failed to remove bundle', error);
    if (button) {
      button.disabled = false;
      button.classList.remove('loya-bundle-remove--loading');
    }
  }
}

function initBundleRemoval() {
  document.addEventListener('click', function (e) {
    const button = e.target.closest('.loya-bundle-remove');
    if (!button) return;
    const bundleId = button.getAttribute('data-loya-bundle-id');
    if (!bundleId) return;
    e.preventDefault();
    removeBundle(bundleId, button);
  });
}

let loyaBypassCheckoutGate = false;

function installCheckoutGate() {
  // Block the cart form's Checkout button. Capture phase so this runs
  // before Horizon's own handling of the submit. Always prevented first,
  // even when everything checks out — once both gates pass, the note is
  // saved and the form is resubmitted via requestSubmit(submitter),
  // which fires a NEW submit event carrying the original submitter
  // (the Checkout button, or Shop Pay-style button) so Shopify still
  // recognizes it as a checkout attempt and not a plain cart update.
  // Plain form.submit() would silently drop that button's name/value
  // and Shopify would just reload /cart instead of proceeding — the
  // loyaBypassCheckoutGate flag lets that resubmission straight through
  // instead of being gated again (and looping).
  document.addEventListener(
    'submit',
    function (e) {
      const target = e.target;
      if (!target || target.id !== 'cart-form') return;

      if (loyaBypassCheckoutGate) {
        loyaBypassCheckoutGate = false;
        return;
      }

      if (cartNeedsDiamond()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showDiamondRequiredModal();
        return;
      }

      if (!isStoryFormComplete()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        updateStoryFormErrorDisplay();
        focusFirstIncompleteStoryField();
        return;
      }

      if (getStoryForm()) {
        const submitter = e.submitter;
        e.preventDefault();
        e.stopImmediatePropagation();
        saveStoryNote().finally(function () {
          loyaBypassCheckoutGate = true;
          if (submitter && typeof target.requestSubmit === 'function') {
            target.requestSubmit(submitter);
          } else {
            target.submit();
          }
        });
      }
    },
    true
  );

  // Shop Pay / PayPal / other dynamic checkout buttons in the cart
  // bypass the form submit entirely, so the listener above can't catch
  // them — hide that block outright while the cart needs a diamond or
  // the story form is incomplete. Re-checked on every cart change and
  // on every story-form edit, since either can flip without a full page
  // reload.
  applyCheckoutGate();
  // Re-check after every cart change (add, quantity, remove). The flag
  // lives in re-rendered cart HTML, so wait for the request to settle
  // and give Horizon a moment to morph the new markup in.
  document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
    const done = event && event.promise ? event.promise : Promise.resolve();
    done
      .catch(function () {})
      .finally(function () {
        setTimeout(applyCheckoutGate, 50);
        setTimeout(applyCheckoutGate, 500);
      });
  });
  const storyFormEl = getStoryForm();
  if (storyFormEl) {
    storyFormEl.addEventListener('input', applyCheckoutGate);
    storyFormEl.addEventListener('change', applyCheckoutGate);
  }
}

function init() {
  initStoryForm();
  initBundleRemoval();
  initStartNewPiece();
  installCheckoutGate();

  if (isOnDiamondPage()) {
    initDiamondPage();
  } else if (isOnJewelryPage()) {
    initJewelryPage();
  }

  if (normalizePath(window.location.pathname) === JEWELRY_CHOOSER_PATH) {
    initCutRedirectPage();
    initLooseDiamondChoice();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

window.LoyaBundle = {
  getPendingDiamond,
  setPendingDiamond,
  clearPendingDiamond,
  getSelectedCut,
  setSelectedCut,
  clearSelectedCut,
};
